import { useCallback, useSyncExternalStore } from 'react';
import { z } from 'zod';
/**
 * Consent, under Québec's Law 25.
 *
 * ── Why this is a domain concern and not a banner ─────────────────────────
 * Law 25 is not a cookie notice. It requires consent that is **explicit,
 * granular, informed, and as easy to withdraw as to give**, and it makes the
 * organisation accountable for demonstrating it. That is a state machine with
 * rules, not a dismissible strip — so the rules live here, next to the rest of
 * the platform's logic, and the strip is just one of its views.
 *
 * ── The four rules the shape enforces ─────────────────────────────────────
 *
 * 1. **Nothing is on by default.** `unknown` is a real state, distinct from
 *    `denied`. A product cannot infer consent from silence, and a boolean would
 *    have made "not asked yet" and "said no" the same value — which is exactly
 *    the bug that ships analytics to somebody who never answered.
 *
 * 2. **Refusing is as cheap as accepting.** There is no `acceptAll()` without a
 *    matching `denyAll()`, and the UI renders them with equal weight. A
 *    prominent Accept beside a buried Manage is a dark pattern the law names.
 *
 * 3. **Granular.** One purpose per key. Bundling analytics with something a
 *    member actually wants is not consent.
 *
 * 4. **Withdrawable at any time**, from the same control that granted it —
 *    hence `openPreferences` being part of the contract rather than a link
 *    somebody remembers to add to a footer.
 *
 * ── What is NOT gated ─────────────────────────────────────────────────────
 * The session token, the theme, the language and the funnel draft are all
 * *strictly necessary* — they are how the product works at all, and Law 25
 * exempts them. They are named here so nobody widens the gate later on a hunch:
 * gating a member's own sign-in behind an analytics banner would be absurd, and
 * gating analytics behind nothing would be unlawful.
 */
export const CONSENT_STORAGE_KEY = 'lb-consent';
/**
 * The purposes that actually require asking. **Currently none.**
 *
 * ── Why this list exists and is empty ─────────────────────────────────────
 * Consent is owed for non-essential processing. Right now this product has
 * none: it sets no cookies at all, every `localStorage` key is strictly
 * necessary (the session token, theme, language, and a signup draft the member
 * is actively filling in), and the one third-party embed — Vimeo — is loaded
 * with `dnt=1`, so it profiles nobody.
 *
 * So a banner today would be asking permission for something we do not do. That
 * is not a harmless extra: it trains people to dismiss the one that matters
 * later, and it occupied a fifth of the viewport on every page.
 *
 * The mechanism stays because the moment an analytics vendor is attached the
 * obligation is real, and rebuilding it then would be rebuilding something that
 * already worked. Adding `'analytics'` to this array is the whole switch — the
 * banner returns, the gate is already enforced, and the E2E coverage already
 * exists.
 */
export const CONSENT_REQUIRED = [];
/** Whether anything in this build actually needs to be asked about. */
export function consentIsRequired() {
    return CONSENT_REQUIRED.length > 0;
}
const DENIED = { analytics: false };
let state = read();
const listeners = new Set();
/**
 * Storage is untrusted input, and is parsed like any other.
 *
 * The same Zod-at-the-boundary rule the transport uses (§4.6). A hand-rolled
 * `typeof` walk over `JSON.parse` would have to get every branch right to
 * arrive at the one answer that matters — and getting it wrong in the
 * permissive direction means honouring a consent record that was never written
 * by this app.
 *
 * `.catch()` on the boolean rather than `.default()`: a malformed value must
 * read as **false**, never as a yes nobody gave.
 */
const StoredConsent = z.object({
    choices: z.object({ analytics: z.boolean().catch(false) }),
    at: z.string(),
});
function read() {
    try {
        // eslint-disable-next-line no-restricted-globals -- the documented owner of this key (§6.5)
        const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (!raw)
            return { status: 'unknown' };
        const parsed = StoredConsent.safeParse(JSON.parse(raw));
        if (parsed.success) {
            return { status: 'answered', choices: parsed.data.choices, at: parsed.data.at };
        }
    }
    catch {
        // Unreadable or malformed. Treated as never asked, which is the safe
        // direction: it re-asks rather than assuming a yes it cannot prove.
    }
    return { status: 'unknown' };
}
function write(next) {
    state = next;
    try {
        if (next.status === 'answered') {
            // eslint-disable-next-line no-restricted-globals -- see above
            localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
        }
        else {
            // eslint-disable-next-line no-restricted-globals -- see above
            localStorage.removeItem(CONSENT_STORAGE_KEY);
        }
    }
    catch {
        // Private mode. The choice still holds for this session; it will be asked
        // again next time, which is the honest failure.
    }
    for (const listener of listeners)
        listener();
}
function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
/**
 * `at` is recorded because Law 25 makes the organisation accountable for
 * demonstrating consent — "they agreed" is not a claim you can support without
 * knowing when, and to which version of the choices.
 */
export function setConsent(choices) {
    write({ status: 'answered', choices, at: new Date().toISOString() });
}
export function acceptAll() {
    setConsent({ analytics: true });
}
export function denyAll() {
    setConsent(DENIED);
}
/** Withdraw entirely — back to never-asked, so the member is asked again. */
export function resetConsent() {
    write({ status: 'unknown' });
}
export function useConsent() {
    return useSyncExternalStore(subscribe, () => state, () => state);
}
/**
 * Whether a purpose may run right now.
 *
 * Reads `unknown` as **no**. That is the whole point: a purpose that runs while
 * the question is still on screen has not been consented to.
 */
export function useConsented(purpose) {
    const current = useConsent();
    return current.status === 'answered' && current.choices[purpose];
}
export function useSetConsent() {
    return useCallback((choices) => setConsent(choices), []);
}
//# sourceMappingURL=consent.js.map